"use client";

// useCoTheme — paint a company room's ground on the document.
//
// The organism engine reads the BODY colour every frame to composite the bloom,
// so a page whose ground lives only on its own element gets a seam where the
// creature meets the page. Every company surface therefore publishes its ground
// twice: as `--bg` on the page element (the menu veil and every frosted surface
// read that) and on `document.body` (the organism reads that). This is the
// second half, and it restores whatever was there when you leave.
//
// ⚠️ AND ON THE CANVAS (<html>), NOT ONLY ON <body>. A body colour PROPAGATES
// to the page canvas only while <html> has none of its own, and what samples
// that canvas is not always what you would expect: Safari draws a
// `backdrop-filter` box against the canvas colour, so the frosted band over the
// crumb came back the house cream on a page whose room is sand — a pale bar the
// exact size of the band, at the top of the page, in Safari only. Painting the
// room on <html> as well leaves nothing for anyone to sample but the room.
//
// ⚠️ AND <body> GOES TRANSPARENT (2026-09-26). The moment <html> carries a
// colour, <body>'s own background stops propagating to the canvas and paints
// its BOX instead — above the fixed `.ground-wash` (z -1), which is how the
// room's wash is drawn. A collapsed top margin started that box ~74px down, so
// the commons showed its night wash in a strip over the crumb and a flat slab
// everywhere below: "the upper part in a different colour". The room lives on
// <html> (the canvas) and the wash rides above it, exactly as on every page
// that never sets a room. netti's ground reader falls back to <html> when the
// body is clear (MemoryOrganism.renderedGroundRGB).
//
// The `--bg` VARIABLE is deliberately not touched here: ThemeProvider is its one
// author (it writes the person's own ground to :root), and a second writer would
// just fight it. This is the painted colour, not the token.

import { useEffect } from "react";

export function useCoTheme(ground: string): void {
  useEffect(() => {
    const root = document.documentElement;
    const prevBody = document.body.style.backgroundColor;
    const prevRoot = root.style.backgroundColor;
    document.body.style.backgroundColor = "transparent";
    root.style.backgroundColor = ground;
    return () => {
      document.body.style.backgroundColor = prevBody;
      root.style.backgroundColor = prevRoot;
    };
  }, [ground]);
}
